/**
 * Sesión con el servidor de REVELA en Cloudflare.
 * - Inicio de sesión con usuario (ej. yesica@revela) y contraseña
 * - La sesión vive en una cookie segura que el navegador no puede leer (HttpOnly)
 * - La primera vez se entra con la contraseña inicial y la app pide elegir una propia
 * En modo demostración no hay servidor: cualquier contraseña entra.
 */
import { useSyncExternalStore } from 'react'
import { DEMO } from './config'

export interface User { usuario: string; nombre: string; temporal?: boolean }
export interface Session { user: User }

let session: Session | null = null
let checking = !DEMO
const listeners = new Set<() => void>()
const emit = () => listeners.forEach(l => l())
function save(s: Session | null) { session = s; checking = false; emit() }

async function call(path: string, body?: unknown) {
  let res: Response
  try {
    res = await fetch(path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new Error('Sin conexión. Revisá internet y probá de nuevo.')
  }
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw Object.assign(new Error(data.error || `Error ${res.status}`), { status: res.status })
  return data
}

/** Al abrir la app: ¿hay una sesión activa? */
export async function checkSession() {
  if (DEMO) return
  try { const { user } = await call('/api/sesion'); save({ user }) } catch { save(null) }
}

export async function signIn(usuario: string, clave: string) {
  if (DEMO) {
    const u = usuario.trim().toLowerCase() || 'yesica@revela'
    save({ user: { usuario: u, nombre: partnerName(u) } })
    return
  }
  const { user } = await call('/api/login', { usuario: usuario.trim().toLowerCase(), clave })
  save({ user })
}

export async function signOut() {
  if (!DEMO) await call('/api/logout', {}).catch(() => {})
  save(null)
}

/** Cambia la contraseña de quien está conectada */
export async function updatePassword(nueva: string) {
  if (DEMO) { if (session) save({ user: { ...session.user, temporal: false } }); return }
  const { user } = await call('/api/clave', { nueva })
  save({ user })
}

/** La otra socia vuelve a entrar con la contraseña inicial */
export async function resetPartnerPassword(usuario: string) {
  if (DEMO) return
  await call('/api/clave', { restablecer: usuario })
}

/** El servidor respondió 401: la sesión venció */
export function sessionExpired() { save(null) }

export function partnerName(u?: string) {
  const s = (u ?? '').toLowerCase()
  if (s.startsWith('leti')) return 'Leticia'
  if (s.startsWith('yesi')) return 'Yesica'
  const base = s.split('@')[0]
  return base ? base.replace(/^./, c => c.toUpperCase()) : 'Socia'
}

export function useSession() {
  const s = useSyncExternalStore(cb => { listeners.add(cb); return () => listeners.delete(cb) }, () => session)
  const c = useSyncExternalStore(cb => { listeners.add(cb); return () => listeners.delete(cb) }, () => checking)
  return { session: s, checking: c, recovery: !!s?.user.temporal, name: s?.user.nombre ?? partnerName(s?.user.usuario) }
}
